import type { ReactNode } from "react"
import styles from "@/styles/components/DataTable.module.css"

/**
 * The hairline table: micro-caps header, one rule per row, an action link on the
 * right.
 *
 * A real `<table>`, not a grid of divs — these are rows of facts about sessions,
 * and a screen reader announcing "column: status" is the whole reason the header
 * exists. The layout is `table-fixed` with per-column widths so the same column
 * lines up across the Customers, Sessions and Reports lists.
 *
 * Rows are not links. The action cell is, because a whole row that navigates
 * makes the participant avatars inside it un-hoverable.
 */

export interface Column<T> {
  key: string
  header: string
  /** A CSS width for the column. Omit for the one column that takes the slack. */
  width?: string
  align?: "left" | "right"
  render: (row: T) => ReactNode
}

interface DataTableProps<T> {
  columns: readonly Column<T>[]
  rows: readonly T[]
  rowKey: (row: T) => string
  /** What the table is, for a screen reader. */
  caption: string
  empty?: ReactNode
}

export default function DataTable<T>({ columns, rows, rowKey, caption, empty }: DataTableProps<T>) {
  if (rows.length === 0 && empty) return <>{empty}</>

  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <caption className={styles.caption}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={column.align === "right" ? styles.right : undefined}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.align === "right" ? styles.right : undefined}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
